/* eslint-disable react/no-array-index-key */
// dependencies
import { List, fromJS, isImmutable, toJS } from "immutable";
import moment from "moment";
import React from "react";
import { connect } from "react-redux";

import { InputWrapper } from "@performyard/py-components";

// components
import Button from "../../../../../components/Button";
import EasyAddFormCard from "../../../../../components/EasyAddFormCard";
import AjaxInput from "../../../../../components/Inputs/AjaxInput/compatibility";
import { SingleSelectForm } from "../../../../../components/Inputs/SelectForm";
import ShowError from "../../../../../components/ShowError";

import { DisplayConfirmationModal } from "../../../../../actions/modals.actions";
// actions
import { addEasyAdd, appendNewForm, deleteEasyAdd, editEasyAdd, removeForm, updateEditingForms } from "../../actions";

import { getEditingForms, getLocalFormsByType } from "../AddFormToCycle/selectors";

// utils
import { checkDuplicateEasyAdd, formatAddEasyAdd } from "../../utils";

import PropTypes from "../../../../../lib/prop-types";

// styles
import css from "./addForm.module.scss";

class AddForm extends React.Component {
	static propTypes = {
		cycleID: PropTypes.string,
		canUserChangeRedactedness: PropTypes.bool.isRequired,
		easyAddForms: PropTypes.instanceOf(List).isRequired,
		appendNewForm: PropTypes.func.isRequired,
		removeForm: PropTypes.func.isRequired,
		deleteEasyAdd: PropTypes.func.isRequired,
		startDate: PropTypes.instanceOf(moment),
		displayModal: PropTypes.func.isRequired,
		addEasyAdd: PropTypes.func.isRequired,
		editEasyAdd: PropTypes.func.isRequired,
		updateEditingForms: PropTypes.func.isRequired,
		editingForms: PropTypes.instanceOf(List).isRequired,
	};

	static defaultProps = {
		startDate: moment(),
		cycleID: null,
	};

	state = {
		reviewFormInput: "",
		expandNewForm: false,
		duplicateEasyAdd: false,
	};

	toggleNewFormContainer = () => {
		this.setState({ expandNewForm: !this.state.expandNewForm });
	};

	handleFormChange = (value) => {
		if (isImmutable(value)) {
			this.setState({ expandNewForm: false, reviewFormInput: "" }, () => {
				this.props.appendNewForm(value, "easyAdd");
			});
		} else {
			this.setState({ reviewFormInput: value });
		}
	};

	showWarning = () => {
		this.setState({ duplicateEasyAdd: true });
		setTimeout(() => {
			this.setState({ duplicateEasyAdd: false });
		}, 5000);
	};

	handleSave = (formID, state, unsaved, index) => {
		const { cycleID } = this.props;
		const currentForm = this.props.easyAddForms.find((form) => form.get("id") === formID);
		const formObject = formatAddEasyAdd(state, currentForm);

		if (unsaved) {
			const hasDuplicate = checkDuplicateEasyAdd(formObject, this.props.easyAddForms, index);
			if (hasDuplicate) {
				this.showWarning();
			} else {
				this.props.addEasyAdd(formID, cycleID, formObject);
			}
		} else {
			const easyAddID = currentForm.get("_id");
			this.props.editEasyAdd(cycleID, easyAddID, formObject);
		}
	};

	toggleEditForm = (formID) => {
		const { editingForms } = this.props;
		const isEditing = editingForms.includes(formID);

		if (isEditing) {
			this.props.updateEditingForms(editingForms.filter((id) => id !== formID));
		} else {
			this.props.updateEditingForms(editingForms.push(formID));
		}
	};

	handleRemoveEasyAdd = async (formID) => {
		const formToDelete = this.props.easyAddForms.find((form) => form.get("id") === formID);
		const formToDeleteId = formToDelete.get("_id");
		const unsavedForm = formToDelete.get("unsaved");
		if (unsavedForm) {
			/**
			 * in edit cycle context an unsaved form is a form added using
			 * AddForm Button. Clicking `Cancel` should remove the form data
			 * and toggle the edit form card.
			 * No server side process is necessary as the
			 * "form" hasn't actually been created yet
			 * */
			this.props.removeForm(formID);
		} else {
			/**
			 * in edit cycle context a form without unsaved=true property
			 * is a form that is currently part of the cycle.
			 * Clicking `Delete` should trigger
			 * deleteEasyAdd
			 * (so that proper server side processes are initiated)
			 * */
			await this.props.displayModal(
				"The 360 Form will be deleted and removed from the review cycle. Are you sure?",
				async () => {
					await this.props.deleteEasyAdd(this.props.cycleID, formToDeleteId);
					this.props.removeForm(formID);
				}
			);
		}
	};

	render() {
		const { expandNewForm, reviewFormInput, duplicateEasyAdd } = this.state;
		const { easyAddForms, editingForms, canUserChangeRedactedness, startDate } = this.props;

		const hasUnsavedForm = easyAddForms.some((form) => form.get("unsaved"));
		const lockedFields = canUserChangeRedactedness ? [] : ["redactedness"];

		return (
			<div className={css.formsContainer}>
				{easyAddForms.map((form, ii) => {
					const formID = form.get("id");
					return (
						<EasyAddFormCard
							className={css.templateForm}
							key={`${form.get("formID")}-${ii}`}
							form={form}
							lockedFields={List(lockedFields)}
							onEdit={() => this.toggleEditForm(formID)}
							onCancel={
								form.get("unsaved", false)
									? () => this.handleRemoveEasyAdd(formID)
									: () => this.toggleEditForm(formID)
							}
							isEditing={editingForms.includes(formID)}
							onDelete={() => this.handleRemoveEasyAdd(formID)}
							onSave={(nextState, unsaved) => this.handleSave(formID, nextState, unsaved, ii)}
							minimumDueDate={startDate}
							isDeleted={!form.getIn(["form", "isActive"], true)}
							invalidMinimumDueDateMessage="The due date and signer date can not be before the review start date."
							isNewForm={form.get("isNewForm")}
						/>
					);
				})}
				{duplicateEasyAdd ? (
					<ShowError
						error={{
							message: 'A 360 form with the same "Who can add this form" field already exists.',
						}}
					/>
				) : null}

				<div className={`${css.addFormContainer} ${expandNewForm ? css.isExpanded : ""}`}>
					{expandNewForm ? (
						<div className={css.reviewFormContainer}>
							<InputWrapper id="addForm" label="Select Review Form">
								<SingleSelectForm
									value={
										reviewFormInput
											? toJS({ name: reviewFormInput?.name, _id: reviewFormInput?.id })
											: null
									}
									onChange={(value) => {
										// convert value to immutable
										value = fromJS({ name: value?.name, id: value?._id });
										this.handleFormChange(value);
									}}
								/>
							</InputWrapper>
							<div className={css.controls}>
								<Button className={css.cancel} color="grey" onClick={this.toggleNewFormContainer}>
									Cancel
								</Button>
							</div>
						</div>
					) : null}
					{hasUnsavedForm || expandNewForm ? null : (
						<div>
							{easyAddForms.size <= 100 ? (
								<div
									className={css.addFormButton}
									onClick={() => this.toggleNewFormContainer("easyAdd")}
								>
									Add New 360 Form
								</div>
							) : null}
						</div>
					)}
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state) => {
	const cycleID = state.navigation.getIn(["page", "values", "cycleId"]);
	return {
		cycleID,
		easyAddForms: getLocalFormsByType(state, "easyAdd"),
		editingForms: getEditingForms(state),
	};
};

const matchDispatchToProps = (dispatch) => ({
	removeForm: (formID) => dispatch(removeForm(formID)),
	appendNewForm: (form, formType) => dispatch(appendNewForm(form, formType)),
	addEasyAdd: (formID, cycleID, payload) => dispatch(addEasyAdd(formID, cycleID, payload)),
	editEasyAdd: (cycleID, easyAddID, form) => dispatch(editEasyAdd(cycleID, easyAddID, form)),
	deleteEasyAdd: (cycleID, easyAddID) => dispatch(deleteEasyAdd(cycleID, easyAddID)),
	updateEditingForms: (formsList) => dispatch(updateEditingForms(formsList)),
	displayModal: (message, action) => dispatch(DisplayConfirmationModal(message, action)),
});

export default connect(mapStateToProps, matchDispatchToProps)(AddForm);
